import type { ReactNode } from "react";
import type { ArrivalBook } from "@pushback-and-chaos/contracts";
import { ARRIVAL, SKILL_LABEL } from "../../design/copy.js";
import { profileReadBack } from "../coach-atoms.js";
import { bookDate, type BookChapter, type CasebookPages } from "./casebook-atoms.js";
import type { PageItem } from "./pages.js";

/**
 * THE ARRIVAL BOOK'S PAGES (2026-10-11). How a learner and AJ met, cut into
 * the same page items the casebook uses, so the one closed-book surface reads
 * it: four chapters, each on a fresh page, in the order the meeting happened.
 * How we met (the intake, both sides, as kept); what AJ read back (the
 * sentences, then the learner's own words each one rests on, then the first
 * focus); the rehearsal (the scene, the move, the reflection, or one honest
 * sentence on why there is none); and the first papers.
 *
 * Every word is the record's own: the server assembled the book from what
 * was kept, and nothing here adds to it. The one mark the script uses for
 * emphasis is dropped rather than drawn, as the coach's pages drop it.
 */

function block(key: string, node: () => ReactNode, breakBefore = false): PageItem {
  return { key, kind: "block", node, breakBefore };
}
function text(key: string, value: string, className: string): PageItem {
  return { key, kind: "text", text: value, className };
}
/** Paragraphs as written: blank lines divide them. */
function paragraphs(value: string): readonly string[] {
  return value.split(/\n\s*\n/).map((p) => p.trim()).filter((p) => p.length > 0);
}
/** The coach's words carry no markup; `*word*` is the script's emphasis, dropped here as coach-atoms.tsx drops it. */
function plain(value: string): string {
  return value.replaceAll("*", "");
}

export function arrivalPages(book: ArrivalBook): CasebookPages {
  const items: PageItem[] = [];
  const chapters: BookChapter[] = [];
  const head = (key: string, label: string): void => {
    items.push(block(key, () => <h2 className="pcs-story-title">{label}</h2>, items.length > 0));
    chapters.push({ key, label });
  };
  const spoke = (key: string, said: string): void => {
    items.push(block(`${key}:who`, () => <p className="pcs-book-speaker">{ARRIVAL.speaker}</p>));
    paragraphs(plain(said)).forEach((paragraph, i) => { items.push(text(`${key}:p${String(i)}`, paragraph, "pcs-prose")); });
  };

  // HOW WE MET: the conversation as kept. A line of the learner's is an
  // exchange, where a page may begin once the chapter runs long.
  head("met:head", ARRIVAL.chapters.met);
  book.conversation.forEach((line, i) => {
    const key = `met:${String(i)}`;
    if (line.from === "learner") items.push({ ...text(`${key}:said`, line.text, "pcs-said"), anchor: true });
    else spoke(key, line.text);
  });

  // WHAT AJ READ BACK: the sentences the learner confirmed, then each quote
  // under what it settled, then the first focus in the learner's words.
  head("read:head", ARRIVAL.chapters.readBack);
  const readBack = book.readBack;
  profileReadBack(readBack, readBack.evidence).forEach((line, i) => { items.push(text(`read:${String(i)}`, line, "pcs-prose")); });
  readBack.evidence.forEach((evidence, i) => {
    const label = ARRIVAL.evidenceLabel[evidence.field] ?? ARRIVAL.evidenceOther;
    items.push(block(`read:ev${String(i)}:label`, () => <p className="pcs-book-thread-name">{label}</p>));
    items.push(text(`read:ev${String(i)}`, evidence.quote, "pcs-prose pcs-quoted"));
  });
  const focus = book.focus;
  if (focus !== null) {
    const skill = SKILL_LABEL[focus.dimension] ?? focus.dimension.replaceAll("_", " ").toLowerCase();
    items.push(block("read:focus:label", () => <p className="pcs-book-thread-name">{ARRIVAL.focusLabel} · {skill}</p>));
    items.push(text("read:focus", focus.text, "pcs-prose pcs-quoted"));
  }

  // THE REHEARSAL: the one move, as made; or why there is none; or, for a
  // learner who has not reached it, nothing at all.
  const rehearsal = book.rehearsal;
  if (rehearsal !== null) {
    head("reh:head", ARRIVAL.chapters.rehearsal);
    items.push(text("reh:scene", rehearsal.scene, "pcs-prose pcs-quoted"));
    items.push({ ...text("reh:move", rehearsal.move, "pcs-said"), anchor: true });
    rehearsal.reflection.forEach((line, i) => { items.push(text(`reh:r${String(i)}`, line, "pcs-prose")); });
  } else if (book.rehearsalAbsence !== null) {
    head("reh:head", ARRIVAL.chapters.rehearsal);
    items.push(text("reh:absent", book.rehearsalAbsence === "skipped" ? ARRIVAL.rehearsalSkipped : ARRIVAL.rehearsalNotKept, "pcs-prose pcs-book-soft"));
  }

  // THE FIRST PAPERS: what AJ said was on the desk, and since when. "the end." turns here.
  const reviewKey = "papers:head";
  head(reviewKey, ARRIVAL.chapters.papers);
  if (book.papers.lines.length > 0) spoke("papers", book.papers.lines.join("\n\n"));
  items.push(text("papers:when", ARRIVAL.papersPlaced(bookDate(book.papers.placedAt)), "pcs-aside"));

  // No room was drawn for this meeting and no document reached the desk: the
  // scene stays as it is and the desk stays empty on every page.
  return {
    items,
    chapters,
    reviewKey,
    sceneFor: () => ({ environment: null, plan: null, cover: true }),
    documentsFor: () => [],
    allDocuments: [],
  };
}
